import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Download, Phone, Newspaper } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { HubHeader, HubFooter } from "../components/HubChrome";
import { useSeo } from "../lib/seo";

const BOILERPLATE =
  "House Heads At Sea is a house music cruise experience founded and hosted by DJ DaVyne and powered by Kompass Events. Each sailing brings House Heads from around the world together for non-stop DJ sets, theme parties, Chicago Steppin, dance classes, group excursions and fellowship at sea. Upcoming voyages: The Escape (Norwegian Breakaway, round-trip New Orleans, December 13–20, 2026), The Europe Experience (Celebrity Equinox, round-trip Barcelona, June 17–26, 2027) and The Getaway (Norwegian Prima, round-trip San Juan, December 4–11, 2027). Learn more at https://househeadsatsea.com.";

const FACTS = [
  { name: "The Escape 2026", rows: ["3rd Annual House Music Cruise", "Norwegian Breakaway • 7 nights", "Round-trip New Orleans • Dec 13–20, 2026", "Cozumel, Roatán, Harvest Caye, Costa Maya"], url: "/escape" },
  { name: "The Europe Experience 2027", rows: ["House Heads At Sea Goes International", "Celebrity Equinox • 9 nights", "Round-trip Barcelona • Jun 17–26, 2027", "Seville, Lisbon (SuncéBeat overnight), Porto, Tangier"], url: "/europe" },
  { name: "The Getaway 2027", rows: ["4th Annual House Music Cruise Experience", "Norwegian Prima • 7 nights", "Round-trip San Juan • Dec 4–11, 2027", "Tortola, St. Kitts, Barbados, St. Lucia, St. Thomas, St. Maarten"], url: "/getaway" },
];

const ASSETS = [
  { label: "Logo — The Getaway 2027", file: "/logo-hhas.webp" },
  { label: "Logo — The Escape 2026", file: "/logo-escape.png" },
  { label: "Logo — The Europe Experience", file: "/logo-europe.png" },
  { label: "2027 Flyer", file: "/flyer.webp" },
  { label: "Ship photo — Norwegian Breakaway", file: "/assets/escape/hero-breakaway.jpg" },
  { label: "Ship photo — Celebrity Equinox", file: "/assets/europe/hero-equinox.jpg" },
  { label: "Ship photo — Norwegian Prima", file: "/prima.jpg" },
];

export default function Press() {
  useSeo("/press");
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(BOILERPLATE); } catch (e) { /* clipboard unavailable */ }
    setCopied(true);
    toast.success("Press description copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white" data-testid="press-page">
      <HubHeader />
      <main className="max-w-5xl mx-auto px-5 py-16 sm:py-20">
        <p className="font-cond uppercase tracking-[0.4em] text-[#E50914] text-sm mb-3 flex items-center gap-2"><Newspaper size={16} /> Press &amp; Media</p>
        <h1 data-testid="press-h1" className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-none">House Heads At Sea <span className="text-[#E50914]">Press Kit</span></h1>
        <p className="mt-5 text-white/65 max-w-2xl">Writers, bloggers, DJs and event listings — use the official description, fast facts, logos and photos below. Please link to househeadsatsea.com when featuring House Heads At Sea.</p>

        <section className="mt-12 bg-[#111111] border border-white/10 rounded-2xl p-6 sm:p-8">
          <div className="flex items-center justify-between gap-4 mb-4">
            <h2 className="font-cond font-bold uppercase tracking-widest text-lg">About House Heads At Sea</h2>
            <button onClick={copy} data-testid="press-copy-boilerplate-btn" className="inline-flex items-center gap-2 border border-white/20 hover:border-[#E50914] rounded-full px-4 py-2 font-cond uppercase tracking-widest text-xs transition-colors">
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p data-testid="press-boilerplate" className="text-white/75 leading-relaxed text-[15px]">{BOILERPLATE}</p>
        </section>

        <section className="mt-12">
          <h2 className="font-cond font-bold uppercase tracking-widest text-lg mb-5">Fast Facts</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {FACTS.map((f) => (
              <Link key={f.name} to={f.url} data-testid={`press-fact-${f.url.slice(1)}`} className="bg-[#111111] border border-white/10 hover:border-[#E50914]/60 rounded-2xl p-6 transition-colors">
                <h3 className="font-display uppercase text-2xl text-[#E50914]">{f.name}</h3>
                <ul className="mt-3 space-y-1.5">{f.rows.map((r) => <li key={r} className="text-white/70 text-sm">{r}</li>)}</ul>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-cond font-bold uppercase tracking-widest text-lg mb-5">Logos &amp; Photos</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {ASSETS.map((a, i) => (
              <a key={a.file} href={a.file} download data-testid={`press-asset-${i}`} className="flex items-center justify-between bg-[#111111] border border-white/10 hover:border-white/30 rounded-xl px-5 py-4 transition-colors">
                <span className="text-white/80 text-sm">{a.label}</span>
                <Download size={16} className="text-[#E50914]" />
              </a>
            ))}
          </div>
        </section>

        <section className="mt-12 bg-[#E50914]/10 border border-[#E50914]/30 rounded-2xl p-6 sm:p-8">
          <h2 className="font-cond font-bold uppercase tracking-widest text-lg">Media Contact</h2>
          <p className="mt-2 text-white/70 text-sm">Interviews with DJ DaVyne, guest DJ bookings, partnerships and event listings.</p>
          <a href="tel:+14706328963" data-testid="press-call-btn" className="mt-5 inline-flex items-center gap-2 bg-[#E50914] hover:bg-[#c20710] rounded-full px-6 py-3 font-cond uppercase tracking-widest text-sm transition-colors">
            <Phone size={16} /> 470-632-8963
          </a>
        </section>
      </main>
      <HubFooter />
      <Toaster position="top-center" richColors />
    </div>
  );
}
